import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_CHAIN_OPTIONS as MASTER_LABEL_CHAIN_OPTIONS } from '../../config/chains';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  MasterLabelUnresolvedRow,
} from '../../types';
import {
  itemNumberFromMasterLabelUrl,
  normalizeMasterLabelUrl,
} from '../../utils/masterLabelLinks';

const ROW_LIMIT = 100;

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatRelativeAge(ageSeconds: number): string {
  if (ageSeconds < 60) return 'just now';
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    const minutes = Math.floor((ageSeconds % 3600) / 60);
    return `${hours}h ${String(minutes).padStart(2, '0')}m ago`;
  }
  const days = Math.floor(ageSeconds / 86400);
  const hours = Math.floor((ageSeconds % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h ago` : `${days}d ago`;
}

function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  ratio: number
): string {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const [red, green, blue] = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * clampedRatio)
  );
  return `rgb(${red}, ${green}, ${blue})`;
}

function snapshotAgeColor(ageSeconds: number): string {
  const oneHour = 60 * 60;
  const oneDay = 24 * oneHour;
  const twoDays = 2 * oneDay;

  if (ageSeconds <= oneHour) {
    return interpolateColor([65, 195, 91], [138, 183, 70], ageSeconds / oneHour);
  }
  if (ageSeconds <= oneDay) {
    return interpolateColor(
      [138, 183, 70],
      [238, 163, 55],
      (ageSeconds - oneHour) / (oneDay - oneHour)
    );
  }
  if (ageSeconds <= twoDays) {
    return interpolateColor([238, 163, 55], [232, 82, 63], (ageSeconds - oneDay) / oneDay);
  }
  return interpolateColor(
    [232, 82, 63],
    [255, 55, 85],
    Math.min(1, (ageSeconds - twoDays) / twoDays)
  );
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatSnapshotStatus(status: string): string | null {
  if (status === 'available') return null;
  if (status === 'stale') return 'Stale';
  if (status === 'missing') return 'No cached snapshot';
  return status;
}

function rowSortKey(row: MasterLabelUnresolvedRow): [number, number, number, string, string] {
  const oldest = row.oldestCreatedAtUtc ? new Date(row.oldestCreatedAtUtc).getTime() : Infinity;
  return [-row.unresolvedCount, -row.pendingCount, oldest, row.chainName, row.rowKey];
}

function sortRows(rows: MasterLabelUnresolvedRow[]): MasterLabelUnresolvedRow[] {
  return [...rows]
    .sort((a, b) => {
      const left = rowSortKey(a);
      const right = rowSortKey(b);
      for (let index = 0; index < left.length; index += 1) {
        const leftValue = left[index];
        const rightValue = right[index];
        if (leftValue < rightValue) return -1;
        if (leftValue > rightValue) return 1;
      }
      return 0;
    })
    .slice(0, ROW_LIMIT);
}

function rowLabel(row: MasterLabelUnresolvedRow): string {
  if (row.itemNumber) return row.itemNumber;
  const linkedItemNumber = itemNumberFromMasterLabelUrl(row.masterLabelUrl);
  if (linkedItemNumber) return linkedItemNumber;
  if (row.rowKind === 'item_number' && row.rowKey) return row.rowKey;
  return 'No item number';
}

function masterLabelUrlForRow(row: MasterLabelUnresolvedRow): string | null {
  return normalizeMasterLabelUrl(row.masterLabelUrl, {
    chainId: row.chainId,
    fallbackItemNumber:
      row.itemNumber ?? (row.rowKind === 'item_number' && row.rowKey ? row.rowKey : undefined),
    requireTarget: true,
  });
}

function recomputeDashboard(
  previous: MasterLabelUnresolvedDashboardResponse,
  updatedChain: MasterLabelUnresolvedChainSnapshot
): MasterLabelUnresolvedDashboardResponse {
  const chainExists = previous.chains.some(
    (chain) => chain.chain.chainId === updatedChain.chain.chainId
  );
  const chains = chainExists
    ? previous.chains.map((chain) =>
        chain.chain.chainId === updatedChain.chain.chainId ? updatedChain : chain
      )
    : [...previous.chains, updatedChain];
  return {
    ...previous,
    chains,
    supportedChainIds: previous.supportedChainIds.includes(updatedChain.chain.chainId)
      ? previous.supportedChainIds
      : [...previous.supportedChainIds, updatedChain.chain.chainId].sort(
          (left, right) => left - right
        ),
    topRows: sortRows(chains.flatMap((chain) => chain.rows ?? [])),
  };
}

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const monoCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  fontFamily: 'var(--font-mono)',
};

const separatorHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  borderRight: '1px solid var(--divider)',
};

const separatorBodyCellStyle: React.CSSProperties = {
  ...monoCellStyle,
  borderRight: '1px solid var(--divider)',
};
const leadingSeparatorHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  borderLeft: '1px solid var(--divider)',
};
const leadingSeparatorBodyCellStyle: React.CSSProperties = {
  ...monoCellStyle,
  borderLeft: '1px solid var(--divider)',
};

const refreshButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 28,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const DistributionPlot: React.FC<{ chain: MasterLabelUnresolvedChainSnapshot }> = ({ chain }) => {
  const [hoveredBucketLabel, setHoveredBucketLabel] = useState<string | null>(null);
  const buckets = chain.rowTotalDistribution ?? [];
  const cropCount = chain.cropCount ?? 0;
  const bucketShares = buckets.map((bucket) => {
    if (bucket.cropShare !== undefined) return bucket.cropShare;
    const unresolvedCount = bucket.unresolvedCount ?? 0;
    return cropCount > 0 ? unresolvedCount / cropCount : 0;
  });
  const hasData = buckets.some((bucket, index) => bucket.rowCount > 0 || bucketShares[index] > 0);

  if (!hasData) {
    return <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>-</span>;
  }
  const hoveredBucket = hoveredBucketLabel
    ? buckets.find((bucket) => bucket.label === hoveredBucketLabel)
    : null;
  const hoveredBucketIndex = hoveredBucket
    ? buckets.findIndex((bucket) => bucket.label === hoveredBucket.label)
    : -1;
  const hoveredShare = hoveredBucketIndex >= 0 ? (bucketShares[hoveredBucketIndex] ?? 0) : 0;

  return (
    <div
      aria-label={`${chain.chain.chainName} unresolved distribution`}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 3,
        width: 154,
        height: 56,
      }}
    >
      {buckets.map((bucket, index) => {
        const share = bucketShares[index] ?? 0;
        const height = share > 0 ? Math.max(5, Math.round(Math.sqrt(share) * 54)) : 5;
        const severityRatio = buckets.length > 1 ? index / (buckets.length - 1) : 0;
        const hasBucketData = bucket.rowCount > 0 || share > 0;
        return (
          <div
            key={bucket.label}
            onMouseEnter={() => setHoveredBucketLabel(bucket.label)}
            onMouseLeave={() => setHoveredBucketLabel(null)}
            style={{
              width: hasBucketData ? 12 : 4,
              height,
              borderRadius: '3px 3px 1px 1px',
              background: interpolateColor([242, 204, 70], [255, 30, 50], severityRatio),
              opacity: hasBucketData ? 0.58 + Math.min(0.4, Math.sqrt(share)) : 0.2,
              transition: 'height 140ms ease, opacity 140ms ease, transform 140ms ease',
              transform: hoveredBucketLabel === bucket.label ? 'translateY(-2px)' : 'none',
            }}
          />
        );
      })}
      {hoveredBucket ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 64,
            zIndex: 5,
            minWidth: 170,
            padding: '7px 9px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface-elevated, var(--surface))',
            boxShadow: '0 8px 22px rgba(0, 0, 0, 0.35)',
            color: 'var(--text-primary)',
            fontSize: 11,
            lineHeight: 1.35,
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 3 }}>Bucket {hoveredBucket.label}</div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {formatNumber(hoveredBucket.rowCount)} items
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {formatNumber(hoveredBucket.unresolvedCount ?? 0)} unresolved
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {(hoveredShare * 100).toFixed(2)}% of crops
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const MasterLabelUnresolvedDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<MasterLabelUnresolvedDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [refreshAllProgress, setRefreshAllProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [topRowsChainFilter, setTopRowsChainFilter] = useState<'all' | number>('all');
  const [currentTime, setCurrentTime] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        const nextDashboard = await ForgeApiService.fetchMasterLabelUnresolved(token);
        if (!cancelled) {
          setLoadError(null);
          setDashboard({
            ...nextDashboard,
            topRows: sortRows(nextDashboard.topRows ?? []),
          });
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Error loading Master Label unresolved list';
          setLoadError(message);
          setDashboard(null);
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [showErrorToast, token]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const totals = useMemo(() => {
    const chains = dashboard?.chains ?? [];
    return chains.reduce(
      (acc, chain) => ({
        crops: acc.crops + (chain.cropCount ?? 0),
        inDesign: acc.inDesign + (chain.inDesignCount ?? 0),
        carton: acc.carton + (chain.cartonCount ?? 0),
        pending: acc.pending + chain.pendingCount,
        unassigned: acc.unassigned + chain.unassignedCount,
        inactive: acc.inactive + (chain.inactiveCount ?? 0),
        unresolved: acc.unresolved + chain.unresolvedCount,
        missing: acc.missing + (chain.status === 'missing' ? 1 : 0),
      }),
      {
        crops: 0,
        inDesign: 0,
        carton: 0,
        pending: 0,
        unassigned: 0,
        inactive: 0,
        unresolved: 0,
        missing: 0,
      }
    );
  }, [dashboard]);

  const chains = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);

  const chainOptions = useMemo(() => {
    const supportedChainIds = new Set(
      dashboard?.supportedChainIds ?? chains.map((chain) => chain.chain.chainId)
    );
    return [...supportedChainIds]
      .sort((left, right) => left - right)
      .map((chainId) => {
        const loadedChain = chains.find((chain) => chain.chain.chainId === chainId);
        const configuredChain = MASTER_LABEL_CHAIN_OPTIONS.find((chain) => chain.id === chainId);
        return {
          chainId,
          label: loadedChain?.chain.chainName ?? configuredChain?.label ?? `Chain ${chainId}`,
          timeZone: loadedChain?.chain.timeZone ?? 'UTC',
        };
      });
  }, [chains, dashboard?.supportedChainIds]);

  const refreshChainIds = useMemo(() => chainOptions.map((chain) => chain.chainId), [chainOptions]);

  const beginRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
  };

  const finishRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => {
      const next = new Set(current);
      next.delete(chainId);
      return next;
    });
  };

  const handleRefreshChain = async (chainId: number) => {
    beginRefreshingChain(chainId);
    try {
      const updatedChain = await ForgeApiService.refreshMasterLabelUnresolved(token, chainId);
      setDashboard((current) =>
        current
          ? recomputeDashboard(current, updatedChain)
          : {
              chains: [updatedChain],
              topRows: sortRows(updatedChain.rows ?? []),
              supportedChainIds: [chainId],
            }
      );
      setLoadError(null);
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error refreshing Master Label unresolved snapshot'
      );
    } finally {
      finishRefreshingChain(chainId);
    }
  };

  const handleRefreshAll = async () => {
    if (refreshChainIds.length === 0 || isRefreshingAll) return;

    setIsRefreshingAll(true);
    setRefreshAllProgress({ completed: 0, total: refreshChainIds.length });
    const failures: string[] = [];

    for (const [index, chainId] of refreshChainIds.entries()) {
      beginRefreshingChain(chainId);
      try {
        const updatedChain = await ForgeApiService.refreshMasterLabelUnresolved(token, chainId);
        setDashboard((current) =>
          current
            ? recomputeDashboard(current, updatedChain)
            : {
                chains: [updatedChain],
                topRows: sortRows(updatedChain.rows ?? []),
                supportedChainIds: [chainId],
              }
        );
        setLoadError(null);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Error refreshing Master Label unresolved snapshot';
        failures.push(`chain ${chainId}: ${message}`);
      } finally {
        finishRefreshingChain(chainId);
        setRefreshAllProgress({ completed: index + 1, total: refreshChainIds.length });
      }
    }

    setIsRefreshingAll(false);
    setRefreshAllProgress(null);
    if (failures.length > 0) {
      showErrorToast(
        `Refresh all finished with ${failures.length}/${refreshChainIds.length} failures. ${failures[0]}`
      );
      return;
    }

  };

  const topRows = useMemo(() => {
    if (!dashboard) return [];
    if (topRowsChainFilter === 'all') return sortRows(dashboard.topRows ?? []);
    const selectedChain = dashboard.chains.find(
      (chain) => chain.chain.chainId === topRowsChainFilter
    );
    return sortRows(selectedChain?.rows ?? []);
  }, [dashboard, topRowsChainFilter]);

  return (
    <div className="page-content page-content--wide rdc-dashboard-page">
      <div className="forge-card rdc-dashboard-hero" style={{ marginBottom: 20 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Master Label
            </div>
            <div className="rdc-dashboard-title">Master Label unresolved</div>
            <div style={{ marginTop: 10, color: 'var(--text-secondary)', maxWidth: 720 }}>
              Cached per-chain snapshots ranked by unresolved item groups needing Master Label
              cleanup. Refresh a chain to regenerate its current snapshot.
            </div>
          </div>
          <div style={{ display: 'grid', gap: 6, color: 'var(--text-secondary)', fontSize: 12 }}>
            <div>
              Browser time:{' '}
              <span style={{ color: 'var(--text-primary)' }}>
                {formatTimestamp(currentTime.toISOString())}
              </span>
            </div>
            <div>
              Supported chains:{' '}
              <span style={{ color: 'var(--text-primary)' }}>
                {dashboard?.supportedChainIds?.join(', ') || '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        className="stats-row"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          marginBottom: 18,
        }}
      >
        {[
          ['Crops', totals.crops],
          ['In design', totals.inDesign],
          ['Carton', totals.carton],
          ['Unresolved', totals.unresolved],
          ['Pending', totals.pending],
          ['Unassigned', totals.unassigned],
          ['Inactive', totals.inactive],
          ['Missing snapshots', totals.missing],
        ].map(([label, value]) => (
          <div key={label} className="stat-card">
            <div className="stat-label">{label}</div>
            <div className="stat-value">{formatNumber(Number(value))}</div>
          </div>
        ))}
      </div>

      <div className="forge-card rdc-dashboard-card" style={{ marginBottom: 18 }}>
        <div
          className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
          style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span>Chains</span>
          <button
            type="button"
            style={{
              ...refreshButtonStyle,
              height: 30,
              opacity: isRefreshingAll || refreshChainIds.length === 0 ? 0.65 : 1,
              cursor: isRefreshingAll || refreshChainIds.length === 0 ? 'not-allowed' : 'pointer',
            }}
            disabled={isRefreshingAll || refreshChainIds.length === 0}
            onClick={() => void handleRefreshAll()}
          >
            <RefreshOutlinedIcon style={{ fontSize: 15 }} />
            {isRefreshingAll && refreshAllProgress
              ? `Refreshing ${refreshAllProgress.completed}/${refreshAllProgress.total}`
              : 'Refresh all'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Chain</th>
                <th style={headerCellStyle}>Snapshot</th>
                <th style={leadingSeparatorHeaderCellStyle}>Crops</th>
                <th style={headerCellStyle}>In Design</th>
                <th style={headerCellStyle}>Carton</th>
                <th style={separatorHeaderCellStyle}>Inactive</th>
                <th style={headerCellStyle}>Pending</th>
                <th style={separatorHeaderCellStyle}>Unassigned</th>
                <th style={headerCellStyle}>Distribution</th>
                <th style={headerCellStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} style={bodyCellStyle}>
                    Loading snapshots...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={10} style={{ ...bodyCellStyle, color: 'var(--danger)' }}>
                    {loadError}
                  </td>
                </tr>
              ) : (
                chains.map((chain) => {
                  const isRefreshing = refreshingChainIds.has(chain.chain.chainId);
                  const isRefreshDisabled = isRefreshingAll || isRefreshing;
                  return (
                    <tr key={chain.chain.chainId}>
                      <td style={bodyCellStyle}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                          {chain.chain.chainName}
                        </div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                          chain {chain.chain.chainId}
                        </div>
                      </td>
                      <td style={bodyCellStyle}>
                        {chain.snapshot ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  color: snapshotAgeColor(chain.snapshot.ageSeconds),
                                  fontWeight: 800,
                                }}
                              >
                                {formatRelativeAge(chain.snapshot.ageSeconds)}
                              </span>
                              {formatSnapshotStatus(chain.status) ? (
                                <span
                                  style={{
                                    color:
                                      chain.status === 'stale'
                                        ? 'var(--warning)'
                                        : 'var(--text-tertiary)',
                                    fontSize: 10,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {formatSnapshotStatus(chain.status)}
                                </span>
                              ) : null}
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatTimestamp(chain.snapshot.computedAtUtc)}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--warning)' }}>
                            {formatSnapshotStatus(chain.status) ?? 'No cached snapshot'}
                          </span>
                        )}
                      </td>
                      <td style={leadingSeparatorBodyCellStyle}>
                        {formatNumber(chain.cropCount ?? 0)}
                      </td>
                      <td style={monoCellStyle}>{formatNumber(chain.inDesignCount ?? 0)}</td>
                      <td style={monoCellStyle}>{formatNumber(chain.cartonCount ?? 0)}</td>
                      <td style={separatorBodyCellStyle}>
                        {formatNumber(chain.inactiveCount ?? 0)}
                      </td>
                      <td style={monoCellStyle}>{formatNumber(chain.pendingCount)}</td>
                      <td style={separatorBodyCellStyle}>{formatNumber(chain.unassignedCount)}</td>
                      <td style={bodyCellStyle}>
                        <DistributionPlot chain={chain} />
                      </td>
                      <td style={bodyCellStyle}>
                        <button
                          type="button"
                          style={{
                            ...refreshButtonStyle,
                            opacity: isRefreshDisabled ? 0.65 : 1,
                            cursor: isRefreshDisabled ? 'not-allowed' : 'pointer',
                          }}
                          disabled={isRefreshDisabled}
                          onClick={() => void handleRefreshChain(chain.chain.chainId)}
                        >
                          <RefreshOutlinedIcon style={{ fontSize: 15 }} />
                          {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="forge-card rdc-dashboard-card">
        <div
          className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
          style={{
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div className="rdc-dashboard-section-heading">
            <WarningAmberOutlinedIcon style={{ fontSize: 18, color: 'var(--warning)' }} />
            <span>Top 100 items</span>
            <span className="rdc-dashboard-count-pill">{formatNumber(topRows.length)} rows</span>
          </div>
          <select
            aria-label="Top 100 chain filter"
            value={topRowsChainFilter}
            onChange={(event) => {
              const value = event.target.value;
              setTopRowsChainFilter(value === 'all' ? 'all' : Number(value));
            }}
            style={{
              height: 32,
              minWidth: 180,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-primary)',
              padding: '0 10px',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <option value="all">All supported chains</option>
            {chainOptions.map((chain) => (
              <option key={chain.chainId} value={chain.chainId}>
                {chain.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Chain</th>
                <th style={headerCellStyle}>Item</th>
                <th style={headerCellStyle}>Pending</th>
                <th style={separatorHeaderCellStyle}>Unassigned</th>
                <th style={headerCellStyle}>Inactive</th>
                <th style={headerCellStyle}>Crops</th>
                <th style={headerCellStyle}>Designs</th>
                <th style={headerCellStyle}>Confirmed</th>
              </tr>
            </thead>
            <tbody>
              {topRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={bodyCellStyle}>
                    {isLoading
                      ? 'Loading item rows...'
                      : loadError
                        ? 'Dashboard failed to load.'
                        : 'No item rows in cached snapshots.'}
                  </td>
                </tr>
              ) : (
                topRows.map((row) => {
                  const masterLabelUrl = masterLabelUrlForRow(row);
                  return (
                    <tr key={`${row.chainId}:${row.rowKind}:${row.rowKey}`}>
                      <td style={bodyCellStyle}>{row.chainName}</td>
                      <td style={monoCellStyle}>
                        {masterLabelUrl ? (
                          <Link
                            to={masterLabelUrl}
                            style={{
                              color: 'var(--accent)',
                              fontWeight: 800,
                              textDecoration: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {rowLabel(row)}
                          </Link>
                        ) : (
                          <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                            {rowLabel(row)}
                          </span>
                        )}
                      </td>
                      <td style={monoCellStyle}>{formatNumber(row.pendingCount)}</td>
                      <td style={separatorBodyCellStyle}>{formatNumber(row.unassignedCount)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.inactiveCount ?? 0)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.totalCropCount ?? 0)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.designCount ?? 0)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.confirmedDesignCount ?? 0)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
